import { faPlus, faSync } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useEffect, useState } from "react";
import { Button, Card, CardBody, CardHeader, Container } from "reactstrap";
import AdvanceTable from "../../../../components/advanceTable/AdvanceTable";
import AdvanceTableWrapper from "../../../../components/advanceTable/AdvanceTableWrapper";
import InformationModal from "../../../../components/InformationModal";
import ConfirmationModal from "../../../../components/ConfirmationModal";
import { externalApiKeysApi } from "../../../../services/externalApiKeysServices";
import {
  CreateCustomerApiKeyModal,
  ApiKeyCreatedSuccessModal,
  ExternalApiKeyDetailsModal,
} from "../../../../components/admin/apiKeys/Customer";
import { normalizeExternalApiKeyList } from "../../../../components/admin/apiKeys/apiKeyPayloadUtils";
import { customerApiKeysColumns } from "./customerApiKeysColumns";

const initConfirmation = {
  isOpen: false,
  onSubmit: null,
  onClose: null,
  title: "",
  body: "",
};

const CustomerApiKeys = () => {
  const [loading, setLoading] = useState(true);
  const [keys, setKeys] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [createdKey, setCreatedKey] = useState(null);
  const [detailsRecord, setDetailsRecord] = useState(null);
  const [editRecord, setEditRecord] = useState(null);
  const [informationModal, setInformationModal] = useState({
    isOpen: false,
    title: "",
    body: "",
  });
  const [confirmationModal, setConfirmationModal] = useState(initConfirmation);

  const onDetails = useCallback((k) => setDetailsRecord(k), []);
  const onEdit = useCallback((k) => setEditRecord(k), []);
  const onRevoke = useCallback((row) => {
    setConfirmationModal({
      isOpen: true,
      confirmColor: "danger",
      title: "Revoke API key",
      body: `<span class="text-center">Revoke the key <strong>${
        row.name || "—"
      }</strong>? This cannot be undone.</span>`,
      onClose: () => setConfirmationModal(initConfirmation),
      onSubmit: async () => {
        try {
          setLoading(true);
          await externalApiKeysApi.revoke(row.id);
          setLoading(false);
          setConfirmationModal(initConfirmation);
          setRefresh((v) => !v);
        } catch {
          setLoading(false);
          setConfirmationModal(initConfirmation);
          setInformationModal({
            isOpen: true,
            title: "Revoke API key",
            body: "The key could not be revoked. Please try again.",
          });
        }
      },
    });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const raw = await externalApiKeysApi.list();
      setKeys(normalizeExternalApiKeyList(raw));
    } catch {
      setInformationModal({
        isOpen: true,
        title: "Error",
        body: "There was an error loading API keys.",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, refresh]);

  return (
    <Container fluid className="flex-grow-1 flex-column d-flex">
      <div className="w-100">
        <Card>
          <CardHeader className="d-flex align-items-center justify-content-between mt-2">
            <div className="text-dark flex-grow-1 d-flex align-items-center">
              <h3 className="mb-0">API External Keys — Customers</h3>
            </div>
            <div className="d-flex align-items-center">
              <Button
                size="sm"
                className="rounded-circle d-flex custom-rounded-button"
                color="primary"
                onClick={() => setRefresh((v) => !v)}
                title="Refresh"
              >
                <FontAwesomeIcon icon={faSync} />
              </Button>
              <Button
                size="sm"
                className="ml-2 rounded-circle d-flex custom-rounded-button"
                color="primary"
                onClick={() => setAddOpen(true)}
                title="Add"
              >
                <FontAwesomeIcon icon={faPlus} />
              </Button>
            </div>
          </CardHeader>
          <CardBody className="overflow-x-auto">
            {!loading && !keys.length ? (
              <div className="text-center text-muted py-3">No API keys</div>
            ) : (
              <AdvanceTableWrapper
                columns={customerApiKeysColumns(onDetails, onRevoke, onEdit)}
                data={keys}
                pageSize={Math.max(keys.length, 1)}
              >
                <AdvanceTable
                  table
                  isLoading={loading}
                  headerClassName="text-muted small"
                  tableProps={{
                    striped: true,
                    className: "mb-0 overflow-hidden",
                  }}
                />
              </AdvanceTableWrapper>
            )}
          </CardBody>
        </Card>
      </div>

      {addOpen && !createdKey ? (
        <CreateCustomerApiKeyModal
          onClose={() => setAddOpen(false)}
          onCreated={(plain) => {
            setAddOpen(false);
            setCreatedKey(plain);
          }}
        />
      ) : null}

      {editRecord ? (
        <CreateCustomerApiKeyModal
          record={editRecord}
          onClose={() => setEditRecord(null)}
          onUpdated={() => {
            setEditRecord(null);
            setRefresh((v) => !v);
          }}
        />
      ) : null}

      {createdKey ? (
        <ApiKeyCreatedSuccessModal
          apiKey={createdKey}
          onDone={() => {
            setCreatedKey(null);
            setRefresh((v) => !v);
          }}
        />
      ) : null}

      {detailsRecord ? (
        <ExternalApiKeyDetailsModal
          record={detailsRecord}
          onClose={() => setDetailsRecord(null)}
        />
      ) : null}

      {informationModal.isOpen ? (
        <InformationModal
          title={informationModal.title}
          body={informationModal.body}
          onClose={() =>
            setInformationModal({ isOpen: false, title: "", body: "" })
          }
        />
      ) : null}

      {confirmationModal.isOpen ? (
        <ConfirmationModal {...confirmationModal} />
      ) : null}
    </Container>
  );
};

export default CustomerApiKeys;
